import React, { useEffect, useState } from 'react';
import { Button, Image, Input, Space, Typography } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';

import { api } from '../api/client';

const { Text } = Typography;

/**
 * SHAR-783 / SHAR-785 — the two passenger date rules, in one place.
 *
 * Both forms (ShareJet booking passengers and charter booking passengers) ask
 * for the same two dates and were letting both of them be nonsense: a date of
 * birth in the future, and a passport that expired last year. antd's
 * `disabledDate` greys the days out in the calendar, which stops the mistake at
 * the point it would be made rather than arguing about it afterwards.
 *
 * Both are exclusive of TODAY, which is what the tickets ask for: a passenger is
 * not born today, and a passport expiring today is not one you travel on
 * tomorrow. Day granularity throughout, so the local clock cannot make the
 * boundary behave differently in the afternoon.
 *
 * The apps enforce the same two rules (SHAR-776 / SHAR-784), and the server is
 * the backstop for both.
 */
export const notPast = (d: Dayjs) => !!d && !d.isBefore(dayjs(), 'day');
export const notFuture = (d: Dayjs) => !!d && !d.isAfter(dayjs(), 'day');

/**
 * Passenger widgets shared by the ShareJet booking screen and the charter
 * screens (SHAR-115/122). They were written for BookingDetailPage and moved
 * here unchanged when the charter pages needed the same behaviour: an admin
 * checking a passport should get the same control in both places, and a second
 * copy would have drifted from this one.
 */

/**
 * SHAR-609 — passenger document thumbnails.
 *
 * Uploads are not guaranteed to be images: a passport scan is often a PDF, and a
 * PDF cannot be drawn in an img tag. Anything that is not a recognised image
 * format therefore renders as a labelled file chip that opens in a new tab,
 * rather than as a broken thumbnail. antd's Image supplies click-to-expand and
 * the preview group for free, so there is no lightbox of our own to maintain.
 *
 * `onStage` is optional: the passengers list is read-only, the edit modal passes
 * a handler and gets a delete affordance on each thumbnail.
 */
/**
 * SHAR-617 — the passenger identity fields are always CAPITALS.
 *
 * A passport's machine-readable zone is upper case, and these values are checked
 * against the document by hand, so a name typed in mixed case is a name that has
 * to be mentally re-cased before it can be compared. The transform happens on
 * the way IN rather than only in the display, so what is stored, what is shown
 * and what is sent to the operator are the same string - a display-only
 * uppercase would leave the stored value and the printed manifest disagreeing.
 *
 * Caret position is left alone: this replaces the whole value on each keystroke,
 * which is safe here because the transform never changes the string's length.
 */
export function UpperInput(props: React.ComponentProps<typeof Input>) {
  return (
    <Input
      {...props}
      onChange={(e) => {
        const upper = e.target.value.toUpperCase();
        if (upper !== e.target.value) e.target.value = upper;
        props.onChange?.(e);
      }}
      style={{ textTransform: 'uppercase', ...(props.style ?? {}) }}
    />
  );
}

/**
 * SHAR-668 - a passenger document's URL cannot go straight into an <img>.
 *
 * Those objects now live behind a guarded route, and a browser does not attach
 * the Authorization header to an image request, so the tag would just fail. The
 * signed URL is fetched over an authenticated call and used instead; it expires
 * on its own, so nothing durable sits in the DOM.
 *
 * Aircraft photographs are untouched - they are public by design and go straight
 * into the tag as before.
 */
const PASSENGER_DOC_MARK = '/api/files/passenger-docs/';

type DocUrlState = { href?: string; failed: boolean };

/**
 * Copilot review — the caller has to be able to tell "still fetching" from "will
 * never resolve". Both used to arrive as `undefined`, and the image thumbnail
 * turned that into nothing at all: a document that failed to resolve vanished
 * from the panel entirely, which is the opposite of what the comment below
 * promised and worse than a placeholder, because nobody can see that a document
 * is there at all.
 */
function useResolvedDocUrl(url: string): DocUrlState {
  const [resolved, setResolved] = useState<string | undefined>(
    url.includes(PASSENGER_DOC_MARK) ? undefined : url,
  );
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
    if (!url.includes(PASSENGER_DOC_MARK)) {
      setResolved(url);
      return;
    }
    // Copilot review, 1 Sep — drop the PREVIOUS document before fetching this
    // one. Without this the hook keeps the last resolved URL while the new
    // signed one is in flight, so a row that swaps passengers shows the
    // previous passenger's passport under this one's name until it lands.
    setResolved(undefined);
    let cancelled = false;
    const name = url.split(PASSENGER_DOC_MARK)[1];
    api
      .get<{ url: string }>(`/files/passenger-docs/${name}/url`)
      .then((r) => {
        if (!cancelled) setResolved(r.data.url);
      })
      .catch(() => {
        // A document that will not resolve renders as the file placeholder
        // rather than a broken image.
        if (!cancelled) {
          setResolved(undefined);
          setFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [url]);
  return { href: resolved, failed };
}

/** The 64x64 frame every document occupies, resolved or not. */
const docBoxStyle: React.CSSProperties = {
  display: 'flex',
  width: 64,
  height: 64,
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #f0f0f0',
  borderRadius: 4,
};

/**
 * SHAR-668/669 (Copilot review) — a NON-image document, opened through the same
 * resolver.
 *
 * Images went through useResolvedDocUrl and PDFs were linked with the raw
 * /api/files/passenger-docs/... path. A browser does not attach the
 * Authorization header when it follows a link, so those documents are private
 * to everyone including the admin looking at them: the tap produced a 401. The
 * anchor now waits for the signed URL, and stays disabled until it has one
 * rather than offering a link that cannot work.
 */
function DocLink({ url, label, children }: { url: string; label: string; children: React.ReactNode }) {
  const { href, failed } = useResolvedDocUrl(url);
  const style = docBoxStyle;
  if (!href) {
    return (
      <span
        style={{ ...style, opacity: 0.45 }}
        title={failed ? `${label} — unavailable` : `${label} — loading`}
      >
        {children}
      </span>
    );
  }
  return (
    <a href={href} target="_blank" rel="noreferrer" style={style} title={label}>
      {children}
    </a>
  );
}

/** One thumbnail, with its URL resolved if it needs resolving. */
function DocThumb({ url, label }: { url: string; label: string }) {
  const { href: src, failed } = useResolvedDocUrl(url);
  if (!src) {
    // Copilot review — an unresolved image keeps its place as the same file
    // placeholder a PDF gets. Returning null removed the document from the
    // screen, so an admin could not tell a passenger with an unreadable passport
    // from one who never uploaded it.
    return (
      <span
        style={{ ...docBoxStyle, opacity: 0.45 }}
        title={failed ? `${label} — unavailable` : `${label} — loading`}
      >
        <FileTextOutlined style={{ fontSize: 22 }} />
      </span>
    );
  }
  return (
    <Image
      src={src}
      alt={label}
      width={64}
      height={64}
      style={{ objectFit: 'cover', borderRadius: 4, border: '1px solid #f0f0f0' }}
    />
  );
}

export function DocThumbs({
  docs,
  staged = [],
  onStage,
}: {
  docs: { url: string; label: string }[];
  staged?: string[];
  onStage?: (url: string) => void;
}) {
  const isImage = (u: string) => /\.(png|jpe?g|webp|gif|bmp|heic|heif)(\?|$)/i.test(u);
  return (
    <Image.PreviewGroup>
      <Space wrap size={8}>
        {docs.map((d) => {
          const marked = staged.includes(d.url);
          return (
            <div key={d.url} style={{ textAlign: 'center', opacity: marked ? 0.4 : 1 }}>
              {isImage(d.url) ? (
                <DocThumb url={d.url} label={d.label} />
              ) : (
                <DocLink url={d.url} label={d.label}>
                  <FileTextOutlined style={{ fontSize: 22 }} />
                </DocLink>
              )}
              <div style={{ fontSize: 11, marginTop: 2 }}>
                <Text type="secondary" delete={marked}>{d.label}</Text>
              </div>
              {/* SHAR-609 (retest, 19 Aug) — the control was an 18px-high link at
                  11px, which QA found too small to operate. It is now a normal
                  default-size button spanning the width of its 64px thumbnail,
                  so the target matches the thing it acts on instead of being a
                  line of small text under it. */}
              {onStage ? (
                <Button
                  size="middle"
                  danger={!marked}
                  style={{ width: 64, marginTop: 4, padding: '0 4px' }}
                  onClick={() => onStage(d.url)}
                >
                  {marked ? 'Undo' : 'Delete'}
                </Button>
              ) : null}
            </div>
          );
        })}
      </Space>
    </Image.PreviewGroup>
  );
}

